import clsx from 'clsx';
import type { OutputThresholds } from '@arcanum-hq/shared-types';
import { formatCurrency, formatMultiple, formatPercent } from '@/lib/format';

export type ThresholdFormat = 'percentage' | 'currency' | 'multiplier';

export interface ThresholdBandProps {
  value: number;
  thresholds: OutputThresholds;
  format: ThresholdFormat;
  className?: string;
}

type Zone =
  | 'error-below'
  | 'warn-below'
  | 'ok'
  | 'warn-above'
  | 'error-above';

const ZONES: readonly Zone[] = [
  'error-below',
  'warn-below',
  'ok',
  'warn-above',
  'error-above',
] as const;

function deriveActiveZone(value: number, t: OutputThresholds): Zone {
  if (t.error?.below !== undefined && value < t.error.below) return 'error-below';
  if (t.error?.above !== undefined && value > t.error.above) return 'error-above';
  if (t.warn?.below !== undefined && value < t.warn.below) return 'warn-below';
  if (t.warn?.above !== undefined && value > t.warn.above) return 'warn-above';
  return 'ok';
}

function formatBoundary(v: number, format: ThresholdFormat): string {
  if (format === 'percentage') return formatPercent(v, 0);
  if (format === 'currency') return formatCurrency(v, 0);
  return formatMultiple(v);
}

function activeZoneColor(zone: Zone): string {
  if (zone === 'error-below' || zone === 'error-above') return 'text-danger';
  if (zone === 'warn-below' || zone === 'warn-above') return 'text-warn';
  return 'text-accent';
}

/**
 * 5-segment block bar visualizing where `value` falls relative to the
 * authored thresholds. Zones map left→right as
 * `error-below | warn-below | ok | warn-above | error-above`.
 *
 * Active zone renders ▓ in the zone's accent color (`text-danger` /
 * `text-warn` / `text-accent`); inactive zones render ░ in `text-text-dim`.
 *
 * When `thresholds.error` is absent, the error zones can never be active —
 * they collapse to neutral ░ (no error boundary defined). Same for
 * `thresholds.warn`.
 *
 * Optional trailing label shows the lower-bound `target` or upper-bound
 * `max` derived from the warn band (whichever is configured). No label
 * when neither is set.
 */
export function ThresholdBand({
  value,
  thresholds,
  format,
  className,
}: ThresholdBandProps) {
  const activeZone = deriveActiveZone(value, thresholds);

  const targetLabel = (() => {
    const w = thresholds.warn;
    if (!w) return null;
    if (w.below !== undefined) return `target ${formatBoundary(w.below, format)}`;
    if (w.above !== undefined) return `max ${formatBoundary(w.above, format)}`;
    return null;
  })();

  const ariaLabel = `Threshold: ${activeZone.replace('-', ' ')}${
    targetLabel ? `, ${targetLabel}` : ''
  }`;

  return (
    <span
      role="img"
      aria-label={ariaLabel}
      data-testid="threshold-band"
      data-active-zone={activeZone}
      className={clsx(
        'inline-flex items-center gap-1 font-mono text-micro',
        className,
      )}
    >
      <span className="inline-flex" aria-hidden="true">
        {ZONES.map((zone) => {
          const isActive = zone === activeZone;
          return (
            <span
              key={zone}
              data-zone={zone}
              data-active={isActive ? 'true' : 'false'}
              className={isActive ? activeZoneColor(zone) : 'text-text-dim'}
            >
              {isActive ? '▓' : '░'}
            </span>
          );
        })}
      </span>
      {targetLabel && <span className="text-text-dim">{targetLabel}</span>}
    </span>
  );
}
